import { estFamilleAction, FAMILLES, familleDe } from '@imp/shared/actions-du-jour';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Bell,
  CheckCheck,
  FileText,
  HardHat,
  History,
  Receipt,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  ACTION_LABELS,
  type Alerte,
  type AlerteAction,
  type AlertesResult,
  type AlerteTraitee,
  computeAlertes,
  enregistrerAlerteTraitee,
  listAlertesTraitees,
  MASQUAGE_JOURS,
  type Priorite,
  PRIORITE_META,
  reactiverAlerte,
} from '@/modules/alertes/api';
import { useAuth } from '@/modules/auth/AuthContext';
import { formatEuro } from '@/modules/catalogue/api';
import { markDevisRelance } from '@/modules/devis/api';
import { markFactureRelance } from '@/modules/factures/api';

const RELANCABLE = new Set(['devis_sans_reponse', 'devis_expire', 'facture_en_retard']);
const ALL = '__all__';

// Minuscules + suppression des accents, pour une recherche tolérante.
const norm = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

function quand(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PERIODES = [
  { v: '7', l: '7 derniers jours' },
  { v: '30', l: '30 derniers jours' },
  { v: '90', l: '3 derniers mois' },
  { v: ALL, l: 'Tout l’historique' },
];

export function AlertesPage() {
  const queryClient = useQueryClient();
  const { profil } = useAuth();
  const [vue, setVue] = useState<'encours' | 'historique'>('encours');
  const [filtre, setFiltre] = useState<Priorite | 'toutes'>('toutes');
  // La famille d'action vient de l'adresse : chaque ligne du bloc « À faire
  // aujourd'hui » du tableau de bord ouvre cette page déjà filtrée.
  const [params, setParams] = useSearchParams();
  const familleDemandee = params.get('famille');
  const famille = estFamilleAction(familleDemandee) ? familleDemandee : null;

  const alertesQ = useQuery<AlertesResult>({ queryKey: ['alertes'], queryFn: computeAlertes });
  const histoQ = useQuery({
    queryKey: ['alertes-traitees'],
    queryFn: () => listAlertesTraitees(),
    enabled: vue === 'historique',
  });

  const auteur = { id: profil?.id ?? null, nom: profil ? `${profil.prenom} ${profil.nom}`.trim() : null };

  const invalider = () => {
    void queryClient.invalidateQueries({ queryKey: ['alertes'] });
    void queryClient.invalidateQueries({ queryKey: ['alertes-traitees'] });
  };

  // Traite une alerte : action métier éventuelle + trace dans l'historique.
  const traiterMut = useMutation({
    mutationFn: async ({ a, action }: { a: Alerte; action: AlerteAction }) => {
      if (action === 'relance') {
        if (a.entite === 'devis') await markDevisRelance(a.entiteId);
        else if (a.entite === 'facture') await markFactureRelance(a.entiteId);
      }
      await enregistrerAlerteTraitee(a, action, auteur);
    },
    onSuccess: invalider,
  });

  const reactiverMut = useMutation({
    mutationFn: (id: string) => reactiverAlerte(id),
    onSuccess: invalider,
  });

  const data = alertesQ.data;
  const deLaFamille = (data?.alertes ?? []).filter(
    (a) => famille === null || familleDe(a.type) === famille,
  );
  // Les compteurs suivent la famille choisie : trois cases « priorité » qui
  // compteraient toutes les alertes au-dessus d'une liste filtrée se contrediraient.
  const counts =
    famille === null
      ? (data?.counts ?? { haute: 0, moyenne: 0, basse: 0, total: 0 })
      : {
          haute: deLaFamille.filter((a) => a.priorite === 'haute').length,
          moyenne: deLaFamille.filter((a) => a.priorite === 'moyenne').length,
          basse: deLaFamille.filter((a) => a.priorite === 'basse').length,
          total: deLaFamille.length,
        };
  const alertes = deLaFamille.filter((a) => filtre === 'toutes' || a.priorite === filtre);

  const cards: { key: Priorite; n: number }[] = [
    { key: 'haute', n: counts.haute },
    { key: 'moyenne', n: counts.moyenne },
    { key: 'basse', n: counts.basse },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Centre d&apos;alertes</h1>
          <p className="text-muted-foreground">
            Suivi des devis et factures à traiter, classés par priorité.
          </p>
        </div>
        <div className="flex gap-1 rounded-lg border bg-card p-1">
          <button
            type="button"
            onClick={() => {
              setVue('encours');
            }}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              vue === 'encours' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            À traiter
          </button>
          <button
            type="button"
            onClick={() => {
              setVue('historique');
            }}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              vue === 'historique' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            <History className="h-4 w-4" /> Historique
          </button>
        </div>
      </div>

      {vue === 'historique' ? (
        <HistoriqueVue
          rows={histoQ.data ?? []}
          isLoading={histoQ.isLoading}
          erreur={histoQ.isError ? messageErreur(histoQ.error) : null}
          onReactiver={(id) => {
            reactiverMut.mutate(id);
          }}
          reactivationEnCours={reactiverMut.isPending}
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            {cards.map(({ key, n }) => {
              const meta = PRIORITE_META[key];
              const active = filtre === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setFiltre(active ? 'toutes' : key);
                  }}
                  className={`flex items-center justify-between rounded-lg border bg-card p-4 text-left transition-colors hover:bg-muted/40 ${active ? 'ring-2 ring-primary' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`h-3 w-3 rounded-full ${meta.dot}`} />
                    <span className="text-sm font-medium">Priorité {meta.label.toLowerCase()}</span>
                  </div>
                  <span className="text-2xl font-bold tabular-nums">{n}</span>
                </button>
              );
            })}
          </div>

          {famille !== null ? (
            <div className="bg-muted flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2 text-sm">
              <span>
                Seulement : <strong>{FAMILLES[famille].plusieurs}</strong>
              </span>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => {
                  setParams({});
                  setFiltre('toutes');
                }}
              >
                Voir toutes les alertes
              </button>
            </div>
          ) : null}

          {filtre !== 'toutes' ? (
            <button
              type="button"
              className="text-sm text-primary hover:underline"
              onClick={() => {
                setFiltre('toutes');
              }}
            >
              ← Toutes les priorités
            </button>
          ) : null}

          {traiterMut.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
              Échec : {messageErreur(traiterMut.error)}
            </div>
          ) : null}

          {alertesQ.isLoading ? (
            <p className="text-muted-foreground">Analyse en cours…</p>
          ) : alertesQ.isError ? (
            <p className="text-destructive">Erreur : {messageErreur(alertesQ.error)}</p>
          ) : alertes.length === 0 ? (
            <Card className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <CheckCheck className="h-8 w-8 text-green-600" />
              <p className="font-medium">Rien à signaler</p>
              <p className="text-sm text-muted-foreground">
                {counts.total === 0
                  ? 'Aucune alerte en cours. Tout est à jour.'
                  : 'Aucune alerte pour ce filtre.'}
              </p>
            </Card>
          ) : (
            <div className="space-y-3">
              {alertes.map((a) => {
                const meta = PRIORITE_META[a.priorite];
                const Icon =
                  a.entite === 'facture' ? Receipt : a.entite === 'chantier' ? HardHat : FileText;
                return (
                  <Card key={a.id} className={`border-l-4 ${meta.border}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4 p-4">
                      <div className="flex gap-3">
                        <div className="mt-0.5 text-muted-foreground">
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${meta.badge}`}>
                              {meta.label}
                            </span>
                            <span className="font-medium">{a.titre}</span>
                          </div>
                          <p className="mt-0.5 text-sm text-muted-foreground">{a.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {a.montant !== undefined && a.montant > 0 ? (
                          <span className="font-semibold tabular-nums">{formatEuro(a.montant)}</span>
                        ) : null}
                        <div className="flex flex-wrap justify-end gap-2">
                          {RELANCABLE.has(a.type) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={traiterMut.isPending}
                              onClick={() => {
                                traiterMut.mutate({ a, action: 'relance' });
                              }}
                            >
                              Marquer relancé
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={traiterMut.isPending}
                            title={`Enregistre le traitement et masque l'alerte ${MASQUAGE_JOURS} jours`}
                            onClick={() => {
                              traiterMut.mutate({ a, action: 'traite' });
                            }}
                          >
                            <CheckCheck className="h-4 w-4" /> Marquer traité
                          </Button>
                          <Button asChild size="sm" variant="outline">
                            <Link to={a.lien}>
                              Voir <ArrowRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Bell className="h-3.5 w-3.5" /> Les alertes se recalculent à chaque ouverture. Une alerte
            traitée est masquée {MASQUAGE_JOURS} jours, puis réapparaît si la situation persiste.
          </p>
        </>
      )}
    </div>
  );
}

function HistoriqueVue({
  rows,
  isLoading,
  erreur,
  onReactiver,
  reactivationEnCours,
}: {
  rows: AlerteTraitee[];
  isLoading: boolean;
  erreur: string | null;
  onReactiver: (id: string) => void;
  reactivationEnCours: boolean;
}) {
  const [q, setQ] = useState('');
  const [type, setType] = useState(ALL);
  const [periode, setPeriode] = useState('30');

  const types = useMemo(
    () => [...new Set(rows.map((r) => r.alerte_type))].sort((a, b) => a.localeCompare(b, 'fr')),
    [rows],
  );

  const needle = norm(q.trim());
  const filtres = useMemo(() => {
    const limite =
      periode === ALL ? 0 : Date.now() - Number(periode) * 86400000;
    return rows.filter((r) => {
      if (limite && new Date(r.traite_at).getTime() < limite) return false;
      if (type !== ALL && r.alerte_type !== type) return false;
      if (needle) {
        const hay = norm(`${r.titre} ${r.client ?? ''} ${r.alerte_type} ${r.traite_par_nom ?? ''}`);
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, needle, type, periode]);

  const actif = needle !== '' || type !== ALL || periode !== '30';
  const reset = () => {
    setQ('');
    setType(ALL);
    setPeriode('30');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="space-y-1.5">
          <label htmlFor="histo-q" className="text-xs font-medium text-muted-foreground">
            Rechercher
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="histo-q"
              placeholder="Titre, client, personne…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
              }}
              className="w-full pl-9 sm:w-64"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="histo-periode" className="text-xs font-medium text-muted-foreground">
            Période
          </label>
          <Select
            id="histo-periode"
            value={periode}
            onChange={(e) => {
              setPeriode(e.target.value);
            }}
            className="sm:w-48"
          >
            {PERIODES.map((p) => (
              <option key={p.v} value={p.v}>
                {p.l}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="histo-type" className="text-xs font-medium text-muted-foreground">
            Type d&apos;alerte
          </label>
          <Select
            id="histo-type"
            value={type}
            onChange={(e) => {
              setType(e.target.value);
            }}
            className="sm:w-56"
          >
            <option value={ALL}>Tous les types</option>
            {types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </div>
        {actif ? (
          <Button variant="outline" onClick={reset}>
            <X className="h-4 w-4" /> Réinitialiser
          </Button>
        ) : null}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Chargement de l&apos;historique…</p>
      ) : erreur ? (
        <p className="text-destructive">Erreur : {erreur}</p>
      ) : filtres.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-2 py-16 text-center">
          <History className="h-8 w-8 text-muted-foreground" />
          <p className="font-medium">Aucune alerte traitée</p>
          <p className="text-sm text-muted-foreground">
            {rows.length === 0
              ? 'L’historique se remplira dès que vous marquerez une alerte comme relancée ou traitée.'
              : 'Aucun résultat pour ces filtres.'}
          </p>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {filtres.length} alerte{filtres.length > 1 ? 's' : ''} traitée
            {filtres.length > 1 ? 's' : ''}
            {rows.length !== filtres.length ? ` sur ${rows.length}` : ''}
          </p>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Traitée le</th>
                  <th className="px-4 py-3 font-medium">Alerte</th>
                  <th className="px-4 py-3 font-medium">Client</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Par</th>
                  <th className="px-4 py-3 text-right font-medium">Montant</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtres.map((r) => {
                  const meta = r.priorite ? PRIORITE_META[r.priorite] : null;
                  const masquee = r.masquer_jusqu_a
                    ? new Date(r.masquer_jusqu_a).getTime() > Date.now()
                    : false;
                  return (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                        {quand(r.traite_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          {meta ? (
                            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${meta.badge}`}>
                              {meta.label}
                            </span>
                          ) : null}
                          <span className="font-medium">{r.titre}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{r.alerte_type}</span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{r.client ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950 dark:text-green-300">
                          {ACTION_LABELS[r.action] ?? r.action}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{r.traite_par_nom ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {r.montant ? formatEuro(r.montant) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {masquee ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={reactivationEnCours}
                            title="Faire réapparaître cette alerte immédiatement"
                            onClick={() => {
                              onReactiver(r.id);
                            }}
                          >
                            <RotateCcw className="h-4 w-4" /> Réafficher
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
